/**
 * Right pane of a pe3d base: the selected variant's editor.
 *
 *   Config    every field of the game's schema (pe3dFields.js)
 *   General Settings  store links, end type, store-in-playable, the language
 *                     label — what versionedPlayEngine's General Settings holds
 *   Assets    the slots, grouped by kind (containerPe3dAssets.js): the game's
 *             file, a file the release ships, or a media library file from
 *             the slot's bucket, picked or uploaded
 *   Release   the game and release the variant is made from, and moving it to
 *             another release of the same game
 *   Agent     the pe3d agent (containerPe3dAgent.js), for users who have the
 *             opt-in agent tab
 *
 * Same chrome as the snapshot editor's and the version editor's right pane
 * (snapshotEditor/containerSnapshotConfig.js): a 44px vertical icon rail, the
 * panel beside it, the inspector's sections and rows (pe3dPanelKit). Saving is
 * automatic (usePe3dEditor) and silent like theirs; only a save that cannot
 * happen — problems, a newer copy elsewhere, a failed request — puts a banner
 * on top.
 */

import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Button, Card, CardContent, CircularProgress, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from "@material-ui/core";
import TuneIcon from "@material-ui/icons/Tune";
import SettingsIcon from "@material-ui/icons/Settings";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import NewReleasesIcon from "@material-ui/icons/NewReleases";
import PaletteIcon from "@material-ui/icons/Palette";
import AssistantIcon from "@material-ui/icons/Assistant";
import SportsEsportsIcon from "@material-ui/icons/SportsEsports";
import WarningIcon from "@material-ui/icons/Warning";
import { BAR_BG, DARK_SCROLLBAR_CSS, PANEL_BG, PROPS_PANEL_CSS, SECTION_DIVIDER } from "../snapshotEditor/panelPalette";
import Pe3dConfigFields from "./pe3dFields";
import ContainerPe3dAssets from "./containerPe3dAssets";
import ContainerPe3dDesign from "./containerPe3dDesign";
import ContainerPe3dAgent from "./containerPe3dAgent";
import { OverrideMark, Pe3dOverrideContext, overrideContextOf } from "./pe3dLanguageMark";
import { LANGUAGE_CAPTIONS } from "../../modules/pe3dLocalization";
import { ERROR_TEXT, FieldRow, MUTED, OUTLINED_BUTTON_STYLE, PanelHint, PanelMessage, SectionHeader } from "./pe3dPanelKit";
import { filterTabDefs } from "../../modules/editorTabAccess";
import { networkCaption } from "../../modules/playEngineExportTargets";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", height: "100%", width: "435px", minWidth: "500px" },
  body: { display: "flex", flex: 1, minHeight: 0, backgroundColor: PANEL_BG, color: "#e6e6e6" },
  rail: { display: "flex", flexDirection: "column", width: 44, flexShrink: 0, overflow: "hidden", borderRight: `1px solid ${SECTION_DIVIDER}` },
  panel: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column" },
  fill: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
  scroll: { flex: 1, minHeight: 0, overflowY: "auto", "&::-webkit-scrollbar": { display: "none" } },
  // Version Settings' sticky component header.
  itemHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    backgroundColor: BAR_BG,
    borderBottom: `1px solid ${SECTION_DIVIDER}`,
    color: "#e6e6e6",
    fontSize: 13,
    minWidth: 0,
  },
  itemName: { fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  itemMeta: { color: MUTED, whiteSpace: "nowrap" },
  // The snapshot editor's locked banner, for a save that is held back.
  banner: { display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", fontSize: 12, borderBottom: `1px solid ${SECTION_DIVIDER}` },
  bannerWarn: { color: "#c8a45c", backgroundColor: "#3b352a" },
  bannerError: { color: "#ef9a9a", backgroundColor: "#3b2a2a" },
  problems: { fontSize: 11, color: ERROR_TEXT, padding: "6px 10px", borderTop: `1px solid ${SECTION_DIVIDER}`, backgroundColor: BAR_BG, maxHeight: 120, overflowY: "auto", lineHeight: 1.5 },
  value: { fontSize: 12, color: "#e6e6e6", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" },
});

// What a save that is held back says; a save on its way is not news.
const BANNERS = {
  invalid: { text: "Not saved — fix the problems listed at the bottom.", tone: "warn" },
  conflict: { text: "Changed elsewhere — reload to see the other copy.", tone: "warn", reload: true },
  error: { text: "Saving failed — the next change tries again.", tone: "error" },
};

const TEXT_INPUT_PROPS = { style: { color: "#fff", fontSize: "12px" } };

// The two the versionedPlayEngine General Settings paints with, so the Playable
// tab and it are the same screen rather than two that merely agree.
const CARD_BG = "#474747";
const FIELD_BG = "#313131";

/**
 * The language every tab edits and the preview shows, in the panel header — as Version Settings'
 * language selector: the variant's languages first (marked), every other one greyed below it;
 * picking one the variant does not have adds it, empty (it follows en until something is set).
 */
function LanguageSelect({ editor, canEdit }) {
  const own = editor.languages;
  const others = Object.keys(LANGUAGE_CAPTIONS).filter((code) => !own.includes(code));
  const captionOf = (code) => LANGUAGE_CAPTIONS[code] || code;
  const pick = (code) => {
    if (code === editor.language) return;
    if (own.includes(code)) editor.setLanguage(code);
    else if (canEdit) editor.addLanguage(code);
  };
  return (
    <TextField
      select
      size="small"
      value={editor.language}
      onChange={(event) => pick(event.target.value)}
      style={{ marginLeft: "auto", flexShrink: 0, minWidth: 150 }}
      InputProps={{ style: { color: "#fff", fontSize: "12px", backgroundColor: "#212226", borderRadius: 4, padding: "0 4px" } }}
      SelectProps={{ renderValue: (code) => captionOf(code), MenuProps: { PaperProps: { style: { backgroundColor: CARD_BG, maxHeight: 420 } } } }}
    >
      {own.map((code) => (
        <MenuItem key={code} value={code} style={{ color: "#90caf9", fontWeight: 600, fontSize: 13 }}>
          {captionOf(code)} ●
        </MenuItem>
      ))}
      {others.map((code) => (
        <MenuItem key={code} value={code} disabled={!canEdit} style={{ color: "#666", fontSize: 13 }}>
          {captionOf(code)}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** The validator's messages for the language being edited: "[tr] path: …" on tr, the unmarked ones on en. */
function problemsOfLanguage(problems, language) {
  const mark = `[${language}] `;
  return language === "en" ? problems.filter((problem) => !problem.startsWith("[")) : problems.filter((problem) => problem.startsWith(mark)).map((problem) => problem.slice(mark.length));
}

/**
 * Playable: the settings a playable carries whatever game it is.
 *
 * Laid out as versionedPlayEngine's General Settings is (Card per group, label
 * above the control), because it holds the same four things its `generalSettings`
 * does — forwardLinks, playableEndType, sipPlayable — and the two are meant to
 * read as one screen. The rest of this pane keeps the inspector rows: those tabs
 * have a versionedPlayEngine twin that uses them too.
 */
function PlayableCard({ title, caption, children }) {
  return (
    <Card style={{ backgroundColor: CARD_BG }}>
      <CardContent>
        <Typography variant="subtitle2" style={{ color: "#fff", fontWeight: 600, marginBottom: caption ? 3 : 10 }}>
          {title}
        </Typography>
        {caption && (
          <Typography variant="caption" style={{ color: "#aaa", display: "block", marginBottom: 12 }}>
            {caption}
          </Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

function PlayableField({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <Typography variant="caption" style={{ color: "#aaa", display: "block", marginBottom: 4 }}>
        {label}
      </Typography>
      {children}
    </div>
  );
}

function PlayableTab({ editor, canEdit }) {
  const { view, doc } = editor;
  const links = view.storeLinks || {};
  const setLink = (platform, link) =>
    editor.editView((variant) => {
      const next = { ...(variant.storeLinks || {}), [platform]: link };
      if (!link) delete next[platform];
      if (_.isEmpty(next)) delete variant.storeLinks;
      else variant.storeLinks = next;
      return variant;
    });
  const setKey = (key, value) =>
    editor.editView((variant) => {
      if (value === undefined) delete variant[key];
      else variant[key] = value;
      return variant;
    });
  return (
    <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 8 }}>
      <PlayableCard title="Store links" caption="Where the playable sends someone who taps through.">
        {[
          ["ios", "App Store"],
          ["android", "Google Play"],
        ].map(([platform, label]) => (
          <PlayableField key={platform} label={<>{label}<OverrideMark kind="storeLinks" fieldKey={platform} disabled={!canEdit} /></>}>
            <TextField
              size="small"
              fullWidth
              disabled={!canEdit}
              value={links[platform] || ""}
              placeholder="Engine default"
              InputProps={TEXT_INPUT_PROPS}
              style={{ backgroundColor: FIELD_BG }}
              onKeyDown={(event) => event.stopPropagation()}
              onChange={(event) => setLink(platform, event.target.value.trim())}
            />
          </PlayableField>
        ))}
      </PlayableCard>

      <PlayableCard title="Ending" caption="What the playable does when it is over.">
        <PlayableField label={<>End type<OverrideMark kind="playable" fieldKey="endType" disabled={!canEdit} /></>}>
          <Select
            fullWidth
            displayEmpty
            disabled={!canEdit}
            value={view.endType || ""}
            style={{ backgroundColor: FIELD_BG, color: "#fff" }}
            onChange={(event) => setKey("endType", event.target.value || undefined)}
          >
            <MenuItem value="">Engine default</MenuItem>
            <MenuItem value="endCard">End card</MenuItem>
            <MenuItem value="directStore">Direct to store</MenuItem>
            <MenuItem value="combined">Combined</MenuItem>
          </Select>
        </PlayableField>
        <PlayableField label={<>Store in playable<OverrideMark kind="playable" fieldKey="sipPlayable" disabled={!canEdit} /></>}>
          <Select
            fullWidth
            displayEmpty
            disabled={!canEdit}
            value={view.sipPlayable === undefined ? "" : String(view.sipPlayable)}
            style={{ backgroundColor: FIELD_BG, color: "#fff" }}
            onChange={(event) => setKey("sipPlayable", event.target.value === "" ? undefined : event.target.value === "true")}
          >
            <MenuItem value="">Engine default</MenuItem>
            <MenuItem value="true">On</MenuItem>
            <MenuItem value="false">Off</MenuItem>
          </Select>
        </PlayableField>
      </PlayableCard>

      {!editor.localizationSupported && (
        <PlayableCard title="Export" caption="This release has no languages; the label only names the export files.">
          <OldLanguageLabel editor={editor} canEdit={canEdit} doc={doc} asCard />
        </PlayableCard>
      )}
    </div>
  );
}

/**
 * A release without languages: the variant is one language, and this only
 * names its export files, as before.
 */
function OldLanguageLabel({ editor, canEdit, doc, asCard = false }) {
  const storedLanguage = _.get(doc, "dashboardMeta.language", "en");
  const [language, setLanguage] = useState(storedLanguage);
  useEffect(() => setLanguage(storedLanguage), [doc && doc._id, storedLanguage]);
  if (asCard) {
    return (
      <PlayableField label="Language">
        <TextField
          size="small"
          fullWidth
          disabled={!canEdit}
          value={language}
          inputProps={{ maxLength: 12 }}
          InputProps={TEXT_INPUT_PROPS}
          style={{ backgroundColor: FIELD_BG }}
          onKeyDown={(event) => event.stopPropagation()}
          onChange={(event) => setLanguage(event.target.value)}
          onBlur={() => language !== storedLanguage && editor.updateMeta(doc._id, { language })}
        />
      </PlayableField>
    );
  }
  return (
    <>
      <SectionHeader title="Export" />
      <FieldRow label="Language">
        <TextField
          size="small"
          fullWidth
          disabled={!canEdit}
          value={language}
          inputProps={{ maxLength: 12 }}
          InputProps={TEXT_INPUT_PROPS}
          onKeyDown={(event) => event.stopPropagation()}
          onChange={(event) => setLanguage(event.target.value)}
          onBlur={() => language !== storedLanguage && editor.updateMeta(doc._id, { language })}
        />
      </FieldRow>
      <PanelHint>
        Only names the export files ({"{languages}"}); the variant carries its own texts. The loading screen is chosen when exporting. This
        release has no languages — publish the game again with a newer engine to add some.
      </PanelHint>
    </>
  );
}

function ReadOnlyValue({ className, children }) {
  return (
    <span className={className} title={typeof children === "string" ? children : undefined}>
      {children}
    </span>
  );
}

function ReleaseTab({ editor, canEdit, classes }) {
  const { doc } = editor;
  // The variant's game and its releases come with the variant list, also for a
  // game that is no longer on the base's list.
  const game = (editor.games || []).find((item) => item.package === doc.game) || null;
  const [target, setTarget] = useState("");
  const [isMoving, setIsMoving] = useState(false);
  const others = game ? game.releases.filter((item) => item.version !== doc.release) : [];
  const info = game && game.releases.find((item) => item.version === doc.release);
  const Value = ({ children }) => <ReadOnlyValue className={classes.value}>{children}</ReadOnlyValue>;

  return (
    <div className="proto-props-panel">
      <style>{PROPS_PANEL_CSS}</style>
      <SectionHeader title="Release" />
      <FieldRow label="Game" readOnly>
        <Value>{game ? game.title : doc.game}</Value>
      </FieldRow>
      <FieldRow label="Package" readOnly>
        <Value>{doc.game}</Value>
      </FieldRow>
      <FieldRow label="Version" readOnly>
        <Value>{doc.release}</Value>
      </FieldRow>
      {info && info.engineVersion && (
        <FieldRow label="Engine" readOnly>
          <Value>{info.engineVersion}</Value>
        </FieldRow>
      )}
      {info && (
        <FieldRow label="Networks" readOnly>
          <Value>{(info.networks || []).map(networkCaption).join(", ")}</Value>
        </FieldRow>
      )}

      {canEdit && others.length > 0 && (
        <>
          <SectionHeader title="Move to another release" />
          <FieldRow
            label="Release"
            action={
              <Button
                size="small"
                variant="outlined"
                disabled={!target || isMoving}
                style={OUTLINED_BUTTON_STYLE}
                onClick={async () => {
                  setIsMoving(true);
                  const moved = await editor.changeRelease(target);
                  setIsMoving(false);
                  if (moved) setTarget("");
                }}
              >
                {isMoving ? <CircularProgress size={14} /> : "Move"}
              </Button>
            }
          >
            <Select fullWidth displayEmpty value={target} onChange={(event) => setTarget(event.target.value)}>
              <MenuItem value="">Choose a release…</MenuItem>
              {others.map((item) => (
                <MenuItem key={item.version} value={item.version}>
                  {item.version}
                </MenuItem>
              ))}
            </Select>
          </FieldRow>
          <PanelHint>
            The variant is checked against that release&apos;s schema first; a field it removed or a bound it tightened must be fixed
            before it can move.
          </PanelHint>
        </>
      )}
    </div>
  );
}

export default function ContainerPe3dConfig({ editor, userPermissions, createSnackbar, userCompany, isAdmin, isPortrait }) {
  const classes = useStyles();
  const theme = useTheme();
  // The agent is an opt-in editor tab with no pe3d key of its own: it follows the
  // playEngine set's "agent" tick. The other tabs are the user's pe3d set.
  const hasAgent = useMemo(() => filterTabDefs([{ key: "agent" }], "playEngine").length > 0, []);
  const canEdit = !userPermissions || userPermissions.editContent !== false;
  const { doc, draft, release } = editor;

  const tabs = useMemo(
    () => [
      ...filterTabDefs(
        [
          { key: "config", label: "Config", icon: <SettingsIcon /> },
          { key: "playable", label: "General Settings", icon: <TuneIcon /> },
          { key: "assets", label: "Assets", icon: <PhotoLibraryIcon /> },
          { key: "design", label: "Design", icon: <PaletteIcon /> },
          { key: "release", label: "Release", icon: <NewReleasesIcon /> },
        ],
        "pe3d"
      ),
      ...(hasAgent ? [{ key: "agent", label: "Agent", icon: <AssistantIcon /> }] : []),
    ],
    [hasAgent]
  );
  const [tabKey, setTabKey] = useState(() => tabs[0].key);

  // The form edits schema defaults ⊕ the variant's patch; the patch is what is stored.
  const { view } = editor;
  const effective = useMemo(
    () => (release && view ? release.code.effectiveConfig(release.bundle.schema.config, view.config) : null),
    [release, view && view.config]
  );
  const onConfigChange = (next) =>
    editor.editView((variant) => {
      const delta = release.code.computeConfigDelta(release.bundle.schema.config, next);
      if (_.isEmpty(delta)) delete variant.config;
      else variant.config = delta;
      return variant;
    });

  const banner = BANNERS[editor.saveState];
  const overrideContext = useMemo(() => overrideContextOf(editor), [editor.localizationSupported, editor.language, editor.isOverridden, editor.resetOverride]);
  const ready = Boolean(doc && release && draft);

  let content;
  if (!doc) content = <PanelMessage>{editor.selectedID ? "Loading the variant…" : "Select a variant on the left to edit it."}</PanelMessage>;
  else if (editor.releaseError) content = <PanelMessage style={{ color: ERROR_TEXT }}>Could not open {doc.game}@{doc.release}: {editor.releaseError}</PanelMessage>;
  else if (tabKey === "agent") content = <div style={{ padding: "8px 10px", height: "100%", boxSizing: "border-box" }}><ContainerPe3dAgent editor={editor} userCompany={userCompany} isAdmin={isAdmin} createSnackbar={createSnackbar} /></div>;
  else if (!ready) content = <PanelMessage>Loading {doc.game}@{doc.release}…</PanelMessage>;
  else if (tabKey === "config") content = <Pe3dConfigFields fields={release.bundle.schema.config} config={effective} problems={problemsOfLanguage(editor.problems, editor.language)} disabled={!canEdit} onChange={onConfigChange} />;
  else if (tabKey === "playable") content = <PlayableTab editor={editor} canEdit={canEdit} />;
  else if (tabKey === "design")
    content = <ContainerPe3dDesign editor={editor} canEdit={canEdit} createSnackbar={createSnackbar} isPortrait={isPortrait} onNavigateToAsset={() => setTabKey("assets")} />;
  else if (tabKey === "assets") content = <ContainerPe3dAssets editor={editor} canEdit={canEdit} createSnackbar={createSnackbar} />;
  else if (tabKey === "release") content = <ReleaseTab editor={editor} canEdit={canEdit} classes={classes} />;

  return (
    <Pe3dOverrideContext.Provider value={overrideContext}>
      <div className={classes.root}>
        <style>{DARK_SCROLLBAR_CSS}</style>
        <div className={classes.body}>
          <div role="tablist" className={classes.rail}>
            {tabs.map((tab) => {
              const selected = tabKey === tab.key;
              return (
                <Tooltip key={tab.key} title={tab.label} placement="right">
                  <IconButton
                    role="tab"
                    aria-selected={selected}
                    aria-label={tab.label}
                    onClick={() => setTabKey(tab.key)}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 0,
                      color: selected ? theme.palette.primary.main : "#bbb",
                      opacity: selected ? 1 : 0.7,
                      borderLeft: selected ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
                      backgroundColor: selected ? "rgba(144, 202, 249, 0.12)" : "transparent",
                    }}
                  >
                    {tab.icon}
                  </IconButton>
                </Tooltip>
              );
            })}
          </div>

          <div className={classes.panel}>
            {doc && (
              <div className={classes.itemHeader}>
                <SportsEsportsIcon style={{ fontSize: 16, color: "#8a8a8a", flexShrink: 0 }} />
                <span className={classes.itemName} title={doc.title}>
                  {doc.title}
                </span>
                <span className={classes.itemMeta}>
                  · {doc.game.replace("@pe3d-games/", "")}@{doc.release}
                </span>
                {editor.localizationSupported && <LanguageSelect editor={editor} canEdit={canEdit} />}
                {(editor.saveState === "saving" || editor.saveState === "pending") && (
                  <Tooltip title="Saving…">
                    <CircularProgress size={12} thickness={6} style={{ marginLeft: editor.localizationSupported ? 0 : "auto", color: "#8ab4c8", flexShrink: 0 }} />
                  </Tooltip>
                )}
              </div>
            )}
            {doc && banner && (
              <div className={`${classes.banner} ${banner.tone === "error" ? classes.bannerError : classes.bannerWarn}`}>
                <WarningIcon style={{ fontSize: 15 }} />
                <span style={{ flex: 1 }}>{banner.text}</span>
                {banner.reload && (
                  <Button size="small" variant="outlined" style={OUTLINED_BUTTON_STYLE} onClick={editor.reload}>
                    Reload
                  </Button>
                )}
              </div>
            )}
            {/* Config scrolls its two panels itself, as Version Settings does. */}
            {ready && (tabKey === "config" || tabKey === "design") ? (
              <div className={classes.fill}>{content}</div>
            ) : (
              <div className={`${classes.scroll} proto-dark-scroll`}>{content}</div>
            )}
            {doc && tabKey !== "agent" && editor.problems.length > 0 && (
              <div className={`${classes.problems} proto-dark-scroll`}>
                {editor.problems.map((problem) => (
                  <div key={problem}>{problem}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Pe3dOverrideContext.Provider>
  );
}
